import { toast } from '@pique/component';
import { EditorSettingService } from '@pique/core/modules/editor-settting';
import { WorkbenchService } from '@pique/core/modules/workbench';
import {
  type DocMode,
  DocsService,
  type LoadedBlockSuiteDoc,
  openDoc,
  useServices,
} from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { DocCollection } from '../../../shared';
import { waitForLoadedDocRoot } from '../block-suite-editor/page-root';

export const usePageHelper = (docCollection: DocCollection) => {
  const { docsService, workbenchService } = useServices({
    DocsService,
    WorkbenchService,
    EditorSettingService,
  });
  const workbench = workbenchService.workbench;
  // const { createDoc } = useDocCollectionHelper(docCollection);
  // keep services ready but avoid early mutations

  const createPageAndOpen = useCallback(
    (
      _mode?: DocMode,
      open?: boolean | 'new-tab'
    ): LoadedBlockSuiteDoc | null => {
      // Create initialized doc record via service
      const record = docsService.createDoc();
      if (open !== false) {
        void (async () => {
          try {
            const { doc, release } = docsService.open(record.id);
            doc.setPriorityLoad(10);
            await doc.waitForSyncReady();
            await waitForLoadedDocRoot(doc.blockSuiteDoc, 500);
            release();
          } catch {
            // ignore readiness errors; best-effort open
          }
          workbench.openDoc(record.id, {
            at: open === 'new-tab' ? 'new-tab' : 'active',
          });
        })().catch(() => {});
      }
      return openDoc(docCollection, record.id);
    },
    [docsService, workbench, docCollection]
  );

  const createEdgelessAndOpen = useCallback(
    (open?: boolean | 'new-tab') => {
      return createPageAndOpen('edgeless', open);
    },
    [createPageAndOpen]
  );

  const importFileAndOpen = useMemo(
    () => async () => {
      const { showImportModal } = await import('@blocksuite/blocks');
      const { promise, resolve, reject } =
        Promise.withResolvers<
          Parameters<
            NonNullable<Parameters<typeof showImportModal>[0]['onSuccess']>
          >[1]
        >();
      const onSuccess = (
        pageIds: string[],
        options: { isWorkspaceFile: boolean; importedCount: number }
      ) => {
        resolve(options);
        toast(
          `Successfully imported ${options.importedCount} Page${
            options.importedCount > 1 ? 's' : ''
          }.`
        );
        if (options.isWorkspaceFile) {
          workbench.openAll();
          return;
        }

        const pageId = pageIds[0];
        if (!pageId) {
          return;
        }
        workbench.openDoc(pageId);
      };
      showImportModal({
        collection: docCollection,
        onSuccess,
        onFail: message => {
          reject(new Error(message));
        },
      });
      return await promise;
    },
    [docCollection, workbench]
  );

  return useMemo(() => {
    return {
      createPage: (mode?: DocMode, open?: boolean | 'new-tab') =>
        createPageAndOpen(mode, open),
      createEdgeless: createEdgelessAndOpen,
      importFile: importFileAndOpen,
    };
  }, [createEdgelessAndOpen, createPageAndOpen, importFileAndOpen]);
};
